import { z } from './zod.js';
export const API_ERROR_CODES = [
    'invalid_request',
    'unauthenticated',
    'forbidden',
    'origin_required',
    'csrf_header_required',
    'smoke_read_only',
    'invalid_credentials',
    'rate_limited',
    'not_found',
    'version_conflict',
    'invalid_body',
    'reference_read_only',
    'worker_timeout',
    'sse_not_mocked',
    'response_contract_violation',
    'internal',
    // 1.0b deliverable 2. `payload_too_large` and `unsupported_media_type` apply implicitly to
    // multipart routes (from stage 1b); every other code is declared by the routes that raise it.
    /** 409: a row-version guard failed; `details.current.rv` holds the row's version now. */
    'row_conflict',
    /** 422: a body's schema version is not one the server reads. */
    'unsupported_schema_version',
    /** 413: an upload over its route's cap. */
    'payload_too_large',
    /** 415: an upload of a type its route does not accept. */
    'unsupported_media_type',
    /**
     * 422: `assets.fetch` refused the scheme or the address. (Deliverable 2's `fetch_failed`, a 502,
     * was withdrawn in stage 2: a failed fetch answers `pending`, never an error.)
     */
    'fetch_refused',
    /** 422: a primary link would make a cycle. */
    'link_cycle',
    /** 422: `isPrimary` on a link type that is not structural. */
    'primary_not_structural',
    /** 409: a field key clashes with a live key. */
    'field_key_conflict',
    /** 422: `session` or `has_card` on a user type. */
    'capability_locked',
    /** 409: a retype while `session_meta` exists. */
    'retype_blocked',
    /** 409: a write to a trashed document. */
    'document_trashed',
    /** 409: an untrash of a live document. */
    'not_in_trash',
    /** 422: a merge into itself or into a merged document. */
    'merge_invalid',
    /** 422: a template of another type. */
    'template_type_mismatch',
    /** 422: a calendar definition fails `compile`; `details.issues` lists its `CalendarIssue`s. */
    'calendar_invalid',
    /** 422: a When input or a calendar file cannot be parsed. */
    'parse_failed',
    /** 409: an export file requested before its job finished. */
    'export_not_ready',
    // CS r4a (the Notion import, DECISIONS 176 and 178), after the closed-list search.
    /**
     * 409: the import run is in a status that refuses this step; `details` holds `status`, and
     * `reason` (with `path` where one applies) when an apply names what it refused.
     */
    'import_state',
    /** 422: an upload's part is missing, or its SHA-256 differs from the one the browser sent. */
    'upload_incomplete',
    // CS r5a (Phase 2b, DECISIONS 212), after the closed-list search.
    /**
     * 409: the session's status refuses this write (an item added to a processed session, a start of
     * a running one); `details` holds `status`, the session's, and `allowed`, the statuses that
     * accept it.
     */
    'session_state',
    // CS r5b (Undo, DECISIONS 212 item 4), after the closed-list search.
    /**
     * 409: `undo.apply` refused; `details` (`UndoRefusedDetailsV1`) holds `reason`, `row_changed` or
     * `not_undoable`, and `rows`, at most 50 `{table, rowId}`.
     */
    'undo_refused',
    // CS r6a (Phase 2c, DECISIONS 213 and 220), after the closed-list search.
    /**
     * 409: a sheet route of one kind named a sheet of the other (`sheets.get` on a character,
     * `sheets.character.get` on a stat block); `details.kind` holds the sheet's kind.
     */
    'wrong_kind',
    /**
     * 409: the Foundry import is in a status that refuses this step (an apply of a rejected
     * preview, an undo of an import that is not the newest); `details` holds `status`, and `reason`
     * where one applies (`not_newest`, `changed_since`). Not r4a's `import_state`, the Notion
     * importer's (open point 47).
     */
    'foundry_import_state',
];
export const ApiErrorCode = z.enum(API_ERROR_CODES);
/** The JSON body of every error response. */
export const ApiErrorBody = z.strictObject({
    code: ApiErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
});
export function isApiErrorCode(value) {
    return typeof value === 'string' && API_ERROR_CODES.includes(value);
}
/**
 * An error response. Server handlers throw it and `bind` serializes it as {@link ApiErrorBody};
 * the client throws it for every non-2xx answer other than 409.
 */
export class ApiError extends Error {
    status;
    code;
    body;
    constructor(status, code, message, extra = {}) {
        if (!Number.isInteger(status) || status < 400 || status > 599) {
            throw new RangeError(`ApiError status must be an integer from 400 to 599, got ${status}`);
        }
        if (!isApiErrorCode(code)) {
            throw new TypeError(`unknown ApiErrorCode "${String(code)}"`);
        }
        const text = message ?? code;
        super(text);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        const body = { code, message: text };
        if (extra.details !== undefined)
            body.details = extra.details;
        if (extra.retryAfterSeconds !== undefined)
            body.retryAfterSeconds = extra.retryAfterSeconds;
        this.body = body;
    }
    /** Rebuild an error from a status and a parsed body. */
    static fromBody(status, body) {
        return new ApiError(status, body.code, body.message, body);
    }
}
/** A 409: the client's base version is stale. `body.details` carries the current version. */
export class ConflictError extends ApiError {
    constructor(body) {
        super(409, body.code, body.message, body);
        this.name = 'ConflictError';
    }
}
/**
 * A success response that breaks the route's contract: a body that fails the response schema,
 * is not JSON, or arrives with another success status. Raised by the client only while it
 * validates responses (every build except production).
 */
export class ResponseContractError extends Error {
    routeId;
    status;
    issues;
    constructor(routeId, status, message, issues = []) {
        super(`${routeId}: ${message}`);
        this.name = 'ResponseContractError';
        this.routeId = routeId;
        this.status = status;
        this.issues = issues;
    }
}
/** The request never produced a response (fetch rejected for a reason other than an abort). */
export class NetworkError extends Error {
    constructor(message, options) {
        super(message, options);
        this.name = 'NetworkError';
    }
}
//# sourceMappingURL=errors.js.map